import {
  ActionViewModel,
  ActionViewModelImplementation,
  durationInSecondsOrZero,
  Effector,
  secondsToDuration,
  uuidOrNewUuid,
} from "./ActionViewModel";
import {
  DirectionToggle,
  StartDelayInput,
  threePointHitchEffectors,
} from "./utils";
import { FormControl, FormLabel, InputAdornment } from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { FormikNumberInput } from "common/components/FormikNumberInput";
import { HitchFloat, QuickAction } from "protos/atk/activities";
import { HitchType } from "protos/atk/enums";
import { RigEffectiveConfig } from "protos/atk/effective_config";
import { TFunction } from "i18next";
import { useRandomId } from "portal/utils/hooks/useRandomId";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import FloatIcon from "common/images/atk-actions/float.svg?react";
import React from "react";
import SetPositionIcon from "common/images/atk-actions/set-position-percent.svg?react";
import TimedForceIcon from "common/images/atk-actions/timed-force.svg?react";

class HitchTimedForceViewModel extends ActionViewModel<HitchTimedForceViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: {
      hitchType: HitchType;
      // separate out sign from the force in the view model so we can provide a
      // radio button for retract/extend
      direction: 1 | -1;
      forcePct: number;
      durationSec: number;
    }
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      hitchTimedForce: {
        hitchType: this.actionData.hitchType,
        forcePct: this.actionData.direction * this.actionData.forcePct,
        duration: secondsToDuration(this.actionData.durationSec),
      },
      carryOverIntoWorkTask: false,
    };
  }
  override actionCardTitle(
    t: TFunction,
    rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.hitchTimedForce"
      );
    }
  }
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.timedForce"
    );
  }

  override components = hitchTimedForce.components;
}
export const hitchTimedForce: ActionViewModelImplementation<HitchTimedForceViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.timedForce",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: threePointHitchEffectors(),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "hitch") {
        throw new Error(
          'hitchTimedForce.newAction: effector type is not "hitch"'
        );
      }

      return new HitchTimedForceViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          hitchType: effector.hitchType,
          direction: 1,
          forcePct: 0,
          durationSec: 0,
        }
      );
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.hitchTimedForce) {
        return new HitchTimedForceViewModel(
          {
            type: "hitch",
            hitchType: action.hitchTimedForce.hitchType,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            hitchType: action.hitchTimedForce.hitchType,
            direction: Math.sign(action.hitchTimedForce.forcePct) >= 0 ? 1 : -1,
            forcePct: Math.abs(action.hitchTimedForce.forcePct),
            durationSec: durationInSecondsOrZero(
              action.hitchTimedForce.duration
            ),
          }
        );
      }
    },
    components: {
      Icon: TimedForceIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const { measurementSystem } = useSelf();
        const directionLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormControl fullWidth className="col-span-full">
              <FormLabel id={directionLabelId}>
                {t(
                  "views.autotractor.autonomousActivities.action.parameterName.direction"
                )}
              </FormLabel>
              <DirectionToggle
                basePath={basePath.actionData.direction}
                variant="three-point"
                aria-labelledby={directionLabelId}
              />
            </FormControl>
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.force"
              )}
              name={basePath.actionData.forcePct.$path}
              numberInputProps={{
                textFieldProps: {
                  slotProps: {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t("utils.units.%")}
                        </InputAdornment>
                      ),
                    },
                  },
                },
              }}
            />
            <FormikDimensionedNumberInput
              name={basePath.actionData.durationSec.$path}
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.duration"
              )}
              dimensionedNumberInputProps={{
                minStoredValue: 0,
                storedUnit: "s",
                localSystem: measurementSystem,
                displayUnits: { imperial: "s", metric: "s" },
              }}
              className="col-start-1"
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };

class HitchSetPositionViewModel extends ActionViewModel<HitchSetPositionViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: {
      hitchType: HitchType;
      positionPct: number;
    }
  ) {
    super(effector, false, startInStageIndex, startDelaySec);
  }

  override toQuickAction(finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      hitchSetPosition: {
        hitchType: this.actionData.hitchType,
        position: this.actionData.positionPct / 100,
      },
      startDelay: secondsToDuration(this.startDelaySec),
      carryOverIntoWorkTask: finalActionThisEffector,
    };
  }
  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex === this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.hitchSetPosition",
        { positionPct: this.actionData.positionPct }
      );
    }
  }
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.setPosition",
      { positionPct: this.actionData.positionPct }
    );
  }
  override components = hitchSetPosition.components;
}
export const hitchSetPosition: ActionViewModelImplementation<HitchSetPositionViewModel> =
  {
    actionButtonLabelI18nKey:
      "views.autotractor.autonomousActivities.action.buttonLabel.setPosition",
    actionMenuSection: "quick-action",
    isNewActionAvailableForEffector: threePointHitchEffectors(),
    newAction: (effector, startInStageIndex) => {
      if (effector.type !== "hitch") {
        throw new Error(
          'hitchSetPosition.newAction: effector type is not "hitch"'
        );
      }

      return new HitchSetPositionViewModel(
        effector,
        startInStageIndex,
        0,
        undefined,
        {
          hitchType: effector.hitchType,
          positionPct: 0,
        }
      );
    },
    fromQuickAction: (action, startInStageIndex) => {
      if (action.hitchSetPosition) {
        return new HitchSetPositionViewModel(
          {
            type: "hitch",
            hitchType: action.hitchSetPosition.hitchType,
          },
          startInStageIndex,
          durationInSecondsOrZero(action.startDelay),
          action.actionId,
          {
            hitchType: action.hitchSetPosition.hitchType,
            positionPct: action.hitchSetPosition.position * 100,
          }
        );
      }
    },
    components: {
      Icon: SetPositionIcon,
      EditForm: ({ basePath }) => {
        const { t } = useTranslation();
        const positionLabelId = useRandomId();

        return (
          <div className="grid grid-cols-2 gap-2">
            <FormikNumberInput
              label={t(
                "views.autotractor.autonomousActivities.action.parameterName.position"
              )}
              name={basePath.actionData.positionPct.$path}
              numberInputProps={{
                textFieldProps: {
                  slotProps: {
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          {t("utils.units.%")}
                        </InputAdornment>
                      ),
                      "aria-labelledby": positionLabelId,
                    },
                  },
                },
              }}
            />
            <StartDelayInput path={basePath.startDelaySec} />
          </div>
        );
      },
    },
  };

class HitchFloatViewModel extends ActionViewModel<HitchFloatViewModel> {
  constructor(
    effector: Effector,
    startInStageIndex: number,
    startDelaySec: number,
    public actionId: string | undefined,
    public actionData: HitchFloat
  ) {
    super(effector, true, startInStageIndex, startDelaySec);
  }

  override toQuickAction(_finalActionThisEffector: boolean): QuickAction {
    return {
      actionId: uuidOrNewUuid(this.actionId),
      startDelay: secondsToDuration(this.startDelaySec),
      hitchFloat: this.actionData,
      carryOverIntoWorkTask: true,
    };
  }
  override actionCardTitle(
    t: TFunction,
    _rigEffectiveConfig: RigEffectiveConfig,
    stageIndex: number,
    _finalActionThisEffector: boolean
  ): string | undefined {
    if (stageIndex >= this.startInStageIndex) {
      return t(
        "views.autotractor.autonomousActivities.action.cardTitle.hitchFloat"
      );
    }
  }
  override effectorBlockedByActionMessage(t: TFunction): string {
    return t(
      "views.autotractor.autonomousActivities.action.blockingMessage.float"
    );
  }
  override components = hitchFloat.components;
}
export const hitchFloat: ActionViewModelImplementation<HitchFloatViewModel> = {
  actionButtonLabelI18nKey:
    "views.autotractor.autonomousActivities.action.buttonLabel.float",
  actionMenuSection: "holding-action",
  isNewActionAvailableForEffector: threePointHitchEffectors(),
  newAction: (effector, startInStageIndex) => {
    if (effector.type !== "hitch") {
      throw new Error('hitchFloat.newAction: effector type is not "hitch"');
    }

    return new HitchFloatViewModel(effector, startInStageIndex, 0, undefined, {
      hitchType: effector.hitchType,
    });
  },
  fromQuickAction: (action, startInStageIndex) => {
    if (action.hitchFloat) {
      return new HitchFloatViewModel(
        {
          type: "hitch",
          hitchType: action.hitchFloat.hitchType,
        },
        startInStageIndex,
        durationInSecondsOrZero(action.startDelay),
        action.actionId,
        action.hitchFloat
      );
    }
  },
  components: {
    Icon: FloatIcon,
    EditForm: ({ basePath }) => {
      return (
        <div className="grid grid-cols-2 gap-2">
          <StartDelayInput path={basePath.startDelaySec} />
        </div>
      );
    },
  },
};
